import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { picturesAPI } from './services/api';
import { parseUTCDate } from './utils/dateUtils';

function PictureUploadModal({ currentUser, selectedParticipant, event, isGroupPhoto, onClose, onUploaded }) {
  const [pictureName, setPictureName] = useState('');
  const [pictureFile, setPictureFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const descriptionInputRef = useRef(null);
  const overlayRef = useRef(null);

  // On iOS, when the keyboard appears the visual viewport shifts but touch coordinates
  // are still in document space. We sync the overlay to the visual viewport so that
  // what the user sees and where touches register are always the same coordinates.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      if (!overlayRef.current) return;
      overlayRef.current.style.top = `${vv.offsetTop}px`;
      overlayRef.current.style.left = `${vv.offsetLeft}px`;
      overlayRef.current.style.width = `${vv.width}px`;
      overlayRef.current.style.height = `${vv.height}px`;
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  const buildAutoDescription = () => {
    const eventName = event?.name || '';
    const eventDateTime = event?.dateTime
      ? parseUTCDate(event.dateTime).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '';
    const fromName = currentUser?.displayName || currentUser?.username || '';
    if (isGroupPhoto) {
      return `from ${fromName} at ${eventName} ${eventDateTime}`.trim();
    }
    const toName = selectedParticipant?.user?.displayName || '';
    return `${toName} from ${fromName} at ${eventName} ${eventDateTime}`.trim();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPictureFile(file);
      setPictureName(buildAutoDescription());
      setTimeout(() => {
        descriptionInputRef.current?.focus();
        descriptionInputRef.current?.select();
      }, 0);
    }
  };

  const handleUpload = async () => {
    if (!pictureFile) {
      alert('Please select an image file');
      return;
    }

    const eventId = event?.id;
    if (!eventId) {
      alert('Missing event information');
      return;
    }

    if (!isGroupPhoto && (!selectedParticipant || !currentUser)) {
      alert('Missing required information');
      return;
    }

    try {
      setUploading(true);

      if (isGroupPhoto) {
        const alreadyExists = await picturesAPI.checkExists(currentUser.id, null, eventId);
        if (alreadyExists) {
          alert('You have already uploaded a group photo for this event');
          onClose();
          return;
        }
        await picturesAPI.upload(pictureFile, pictureName, null, currentUser.id, eventId);
        onUploaded({ type: 'group', eventId });
      } else {
        const alreadyExists = await picturesAPI.checkExists(currentUser.id, selectedParticipant.user.id, eventId);
        if (alreadyExists) {
          alert('You have already uploaded a picture for this participant in this event');
          onUploaded({ type: 'participant', userId: selectedParticipant.user.id });
          onClose();
          return;
        }
        await picturesAPI.upload(pictureFile, pictureName, selectedParticipant.user.id, currentUser.id, eventId);
        onUploaded({ type: 'participant', userId: selectedParticipant.user.id });
      }

      onClose();
    } catch (error) {
      console.error('Error uploading picture:', error);
      const errorMessage = error.response?.data || 'Failed to upload picture. Please try again.';
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const title = isGroupPhoto
    ? 'Upload Event Photo'
    : `Add Image for ${selectedParticipant?.user?.displayName}`;

  return createPortal(
    <div ref={overlayRef} className="upload-picture-overlay" onClick={onClose}>
      <div className="upload-picture-dialog" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h4>{title}</h4>
          <button className="close-dialog" onClick={onClose}>×</button>
        </div>

        <div className="upload-form">
          <div className="form-group">
            <label>Description</label>
            <input
              ref={descriptionInputRef}
              type="text"
              value={pictureName}
              onChange={(e) => setPictureName(e.target.value)}
              placeholder="Enter description (optional)"
              className="picture-name-input"
            />
          </div>

          <div className="form-group">
            <label>Select Image File</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
            />
          </div>

          <div className="upload-actions">
            <button
              className="cancel-upload-btn"
              onClick={onClose}
              onTouchEnd={(e) => { e.preventDefault(); onClose(); }}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              className="upload-btn"
              onClick={handleUpload}
              onTouchEnd={(e) => { e.preventDefault(); if (!uploading && pictureFile) handleUpload(); }}
              disabled={uploading || !pictureFile}
            >
              {uploading ? 'Uploading...' : 'Upload Picture'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PictureUploadModal;
